import type * as ScratchGUIStandalone from "@scratch/scratch-gui/standalone";
import { useEffect, useRef } from "react";

import { createStandaloneEditorProps } from "./editor-config";
import { locale } from "./locale";
import { trackTelemetryAction } from "./telemetry";

declare global {
    interface Window {
        GUI: typeof ScratchGUIStandalone;
    }
}

export interface ScratchVm {
    loadProject: (data: ArrayBuffer | string) => Promise<void>;
    on: (event: "EXTENSION_ADDED" | "PROJECT_CHANGED" | "PROJECT_RUN_START" | "PROJECT_RUN_STOP", listener: () => void) => void;
    postIOData?: (device: string, data: Record<string, unknown>) => void;
    removeListener: (event: "EXTENSION_ADDED" | "PROJECT_CHANGED" | "PROJECT_RUN_START" | "PROJECT_RUN_STOP", listener: () => void) => void;
    renderer?: { canvas?: HTMLCanvasElement; draw: () => void };
    saveProjectSb3: () => Promise<Blob>;
}

export const ScratchEditor = ({
    onVmInit,
}: {
    onVmInit: (vm: ScratchVm) => void;
}) => {
    const editorTarget = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!editorTarget.current) return;
        const { EditorState, createStandaloneRoot, setAppElement } = window.GUI;
        setAppElement(editorTarget.current);
        const editorRoot = createStandaloneRoot(
            new EditorState({ locale, showTelemetryModal: false }),
            editorTarget.current,
        );
        const telemetryListeners = {
            EXTENSION_ADDED: () => trackTelemetryAction("extension_add", { result: "success" }),
            PROJECT_RUN_START: () => trackTelemetryAction("project_run_start"),
            PROJECT_RUN_STOP: () => trackTelemetryAction("project_run_stop"),
        };
        let initializedVm: ScratchVm | undefined;
        editorRoot.render(createStandaloneEditorProps({
            onVmInit: (vm: ScratchVm) => {
                initializedVm = vm;
                for (const [event, listener] of Object.entries(telemetryListeners))
                    vm.on(event as keyof typeof telemetryListeners, listener);
                onVmInit(vm);
            },
        }));
        return () => {
            for (const [event, listener] of Object.entries(telemetryListeners))
                initializedVm?.removeListener(event as keyof typeof telemetryListeners, listener);
            editorRoot.unmount();
        };
    }, [onVmInit]);
    return <div className="scratch-editor" ref={editorTarget} />;
};
